import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form } from "formik";
import CardHeader from "@commons/businesscardHeader";
import Fields from "@components/fields";
import Row from "@components/row";
import Typography from "@components/typography";
import Wrapper from "@components/wrapper";
import { toast } from "react-toastify";
import generalSettings from "@apis/generalSettings";
import useIsMobile from "@hooks/useIsMobile";
import { PAYMENT_METHODS } from "@utils/paymentConfigs";
import { FieldArray } from "formik";
import { Plus, Trash } from "lucide-react";
import TimeSelector from "@commons/time-selector";

const GLOBAL_PAYMENT_METHODS = [
  {
    key: "esewa",
    label: "eSewa",
    color: "#16A34A",
    bg: "#F0FDF4",
    border: "#BBF7D0",
  },
  {
    key: "khalti",
    label: "Khalti",
    color: "#9333EA",
    bg: "#FAF5FF",
    border: "#E9D5FF",
  },
  {
    key: "cod",
    label: "Cash / COD",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
  },
  {
    key: "partial",
    label: "Partial Pay",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
  },
  {
    key: "wallet",
    label: "Wallet",
    color: "#EC4899",
    bg: "#FDF2F8",
    border: "#FBCFE8",
  },
];

const OrderSettings = () => {
  const [initialValues, setInitialValues] = useState({
    slots: [] as any[],
    isPartialPayEnabled: false,
    partialPayAmount: 0,
    partialPayType: "fixed", 
  });

  const [settings, setSettings] = useState<any[]>([]);
  const [scheduleItem, setScheduleItem] = useState<any>(null);
  const [partialPayItem, setPartialPayItem] = useState<any>(null);
  const [globalPaymentItem, setGlobalPaymentItem] = useState<any>(null);
  const [globalMethods, setGlobalMethods] = useState<Record<string, boolean>>({
    esewa: true,
    khalti: true,
    cod: true,
    partial: true,
    wallet: true,
  });
  const isMobile = useIsMobile(800);

  const fetchOrderSettings = useCallback(async () => {
    try {
      const [scheduleRes, partialPayRes, globalPayRes] = await Promise.all([
        generalSettings.getByKey({
          state: "payment-settings",
          id: "payment-schedule",
          showErr: false,
        }),
        generalSettings.getByKey({
          state: "payment-settings",
          id: "partial-pay-settings",
          showErr: false,
        }),
        generalSettings.getByKey({
          state: "payment-settings",
          id: "global-payment-methods",
          showErr: false,
        }),
      ]);

      const values: any = {
        slots: [],
        isPartialPayEnabled: false,
        partialPayAmount: 0,
        partialPayType: "fixed",
      };

      if (scheduleRes && scheduleRes.id) {
        setScheduleItem(scheduleRes);
        values.slots = scheduleRes?.infos?.slots || [];
      } else {
        setScheduleItem(null);
      }

      if (partialPayRes && partialPayRes.id) {
        setPartialPayItem(partialPayRes);
        values.isPartialPayEnabled = partialPayRes?.infos?.isEnabled || false;
        values.partialPayAmount = partialPayRes?.infos?.amount || 0;
        values.partialPayType = partialPayRes?.infos?.type || "fixed";
      } else {
        setPartialPayItem(null);
      }

      if (globalPayRes && globalPayRes.id) {
        setGlobalPaymentItem(globalPayRes);
        setGlobalMethods({
          esewa: true,
          khalti: true,
          cod: true,
          partial: true,
          wallet: true,
          ...(globalPayRes?.infos?.methods || {}),
        });
      } else {
        setGlobalPaymentItem(null);
      }

      setInitialValues(values);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load settings");
    }
  }, []);

  useEffect(() => {
    fetchOrderSettings();
  }, [fetchOrderSettings]);

  const saveGlobalMethods = async (updated: Record<string, boolean>) => {
    const payload = {
      group: "payment-settings",
      key: "global-payment-methods",
      value: "global-payment-methods",
      infos: { methods: updated },
    };
    if (!globalPaymentItem || !globalPaymentItem.id) {
      const res = await generalSettings.create({
        state: "payment-settings",
        data: payload,
      });
      setGlobalPaymentItem(res);
    } else {
      await generalSettings.update({
        state: "payment-settings",
        id: globalPaymentItem.id,
        data: payload,
      });
    }
  };

  const toggleGlobalMethod = async (key: string) => {
    const updated = { ...globalMethods, [key]: !globalMethods[key] };
    setGlobalMethods(updated);
    await saveGlobalMethods(updated);
  };

  const saveSettings = async (values: any) => {
    try {
      // 1. Save Schedule
      const schedulePayload = {
        group: "payment-settings",
        key: "payment-schedule",
        value: "payment-schedule",
        infos: {
          enableSchedule: true,
          slots: values.slots,
        },
      };

      if (!scheduleItem || !scheduleItem.id) {
        await generalSettings.create({
          state: "payment-settings",
          data: schedulePayload,
        });
      } else {
        await generalSettings.update({
          state: "payment-settings",
          id: scheduleItem.id,
          data: schedulePayload,
        });
      }

      // 2. Save Partial Pay
      const partialPayPayload = {
        group: "payment-settings",
        key: "partial-pay-settings",
        value: "partial-pay-settings",
        infos: {
          isEnabled: true,
          amount: values.partialPayAmount,
          type: values.partialPayType,
        },
      };

      if (!partialPayItem || !partialPayItem.id) {
        await generalSettings.create({
          state: "payment-settings",
          data: partialPayPayload,
        });
      } else {
        await generalSettings.update({
          state: "payment-settings",
          id: partialPayItem.id,
          data: partialPayPayload,
        });
      }

      toast.success("Payment settings updated successfully");
      fetchOrderSettings();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update payment settings");
    }
  };

  const paymentSettings = [
    ...PAYMENT_METHODS,
    {
      key: "partial",
      label: "Partial Pay",
      color: "#2563EB",
    },
  ].filter((m) => {
    if (m.key === "esewa") return globalMethods.esewa !== false;
    if (m.key === "khalti") return globalMethods.khalti !== false;
    if (m.key === "cod") return globalMethods.cod !== false;
    if (m.key === "partial") return globalMethods.partial !== false;
    if (m.key === "wallet") return globalMethods.wallet !== false;
    return true;
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={saveSettings}
    >
      {({ values, setFieldValue, submitForm }) => (
        <Form>
          <CardHeader title="Order Settings" />
          <Wrapper>
            <div
              style={{
                marginTop: "10px",
              }}
            >
              {/* ── Global Payment Methods ── */}
              <Row styleRow="justify-content:space-between;align-items:center;margin-bottom:12px">
                <div>
                  <Typography variant="h6">Global Payment Methods</Typography>
                  <Typography color="#64748B">
                    Enable or disable payment methods globally — only enabled
                    methods appear in the schedule below
                  </Typography>
                </div>
              </Row>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  marginBottom: "32px",
                }}
              >
                {GLOBAL_PAYMENT_METHODS.map((method) => {
                  const isOn = globalMethods[method.key] !== false;
                  return (
                    <div
                      key={method.key}
                      onClick={() => toggleGlobalMethod(method.key)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 18px",
                        borderRadius: "12px",
                        border: `1px solid ${isOn ? method.border : "#E2E8F0"}`,
                        background: isOn ? method.bg : "#F8FAFC",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        minWidth: "150px",
                        opacity: isOn ? 1 : 0.55,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: isOn ? method.color : "#94A3B8",
                          flex: 1,
                        }}
                      >
                        {method.label}
                      </span>
                      {/* Toggle switch */}
                      <div
                        style={{
                          position: "relative",
                          width: "40px",
                          height: "22px",
                          borderRadius: "11px",
                          background: isOn ? method.color : "#CBD5E1",
                          transition: "background 0.2s ease",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: "3px",
                            left: isOn ? "21px" : "3px",
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            background: "#FFF",
                            transition: "left 0.2s ease",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  height: "1px",
                  background: "#E2E8F0",
                  marginBottom: "32px",
                }}
              />

              <Row styleRow="justify-content:space-between;align-items:center;margin-bottom:20px">
                <div>
                  <Typography variant="h6">Payment Schedule</Typography>
                  <Typography color="#64748B">
                    Configure time-specific availability for payment methods
                  </Typography>
                </div>
              </Row>

              <FieldArray
                name="slots"
                render={(arrayHelpers) => (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    {values.slots.map((slot: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          padding: "20px",
                          border: "1px solid #E2E8F0",
                          borderRadius: "12px",
                          background: "#F8FAFC",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isMobile
                              ? "1fr"
                              : "2fr 3fr 40px",
                            gap: "20px",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              alignItems: "center",
                            }}
                          >
                            <TimeSelector
                              label="From"
                              value={slot.timeSlot.split(" - ")[0]}
                              onChange={(val) => {
                                const parts = slot.timeSlot.split(" - ");
                                setFieldValue(
                                  `slots.${index}.timeSlot`,
                                  `${val} - ${parts[1] || "12:00 PM"}`,
                                );
                              }}
                            />
                            <span style={{ paddingTop: "20px" }}>-</span>
                            <TimeSelector
                              label="To"
                              value={slot.timeSlot.split(" - ")[1]}
                              onChange={(val) => {
                                const parts = slot.timeSlot.split(" - ");
                                setFieldValue(
                                  `slots.${index}.timeSlot`,
                                  `${parts[0] || "12:00 AM"} - ${val}`,
                                );
                              }}
                            />
                          </div>

                          <div>
                            <label
                              style={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#64748B",
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              Active Payment Methods
                            </label>
                            <div
                              style={{
                                display: "flex",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              {paymentSettings.map((method) => (
                                <label
                                  key={method.key}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    background: "#FFF",
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    border: "1px solid #E2E8F0",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={slot.methods.includes(method.key)}
                                    onChange={(e) => {
                                      const newMethods = e.target.checked
                                        ? [...slot.methods, method.key]
                                        : slot.methods.filter(
                                            (m: string) => m !== method.key,
                                          );
                                      setFieldValue(
                                        `slots.${index}.methods`,
                                        newMethods,
                                      );
                                    }}
                                  />
                                  <span style={{ fontSize: "13px" }}>
                                    {method.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              arrayHelpers.remove(index);
                              submitForm();
                            }}
                            style={{
                              background: "#FEF2F2",
                              border: "1px solid #FEE2E2",
                              color: "#EF4444",
                              padding: "8px",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </div>
                    ))}

                    <div
                      onClick={() =>
                        arrayHelpers.push({
                          timeSlot: "09:00 AM - 10:00 AM",
                          methods: [],
                        })
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "#16A34A",
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: "10px",
                        border: "1px dashed #16A34A",
                        borderRadius: "12px",
                        justifyContent: "center",
                        background: "#F0FDF4",
                      }}
                    >
                      <Plus size={20} />
                      <span>Add Time Slot</span>
                    </div>
                  </div>
                )}
              />

              <div
                style={{
                  height: "1px",
                  background: "#E2E8F0",
                  margin: "40px 0",
                }}
              />

              {globalMethods.partial !== false && (
                <>
                  {/* <Row styleRow="justify-content:space-between;align-items:center;margin-bottom:20px">
                <div>
                  <Typography variant="h6">Partial Payment</Typography>
                  <Typography color="#64748B">
                    Configure upfront portion details for partial payments
                  </Typography>
                </div>
              </Row> */}

                  <div
                    style={{
                      padding: "20px",
                      border: "1px solid #E2E8F0",
                      borderRadius: "12px",
                      background: "#F8FAFC",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                          gap: "20px",
                          animation: "fadeIn 0.3s ease",
                        }}
                      >
                        <div>
                          <label
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#64748B",
                              marginBottom: "8px",
                              display: "block",
                            }}
                          >
                            Partial Amount
                          </label>
                          <input
                            type="number"
                            value={values.partialPayAmount}
                            onChange={(e) =>
                              setFieldValue("partialPayAmount", e.target.value)
                            }
                            placeholder="Enter amount"
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "8px",
                              border: "1px solid #E2E8F0",
                              fontSize: "14px",
                            }}
                          />
                        </div>

                        <div>
                          <label
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#64748B",
                              marginBottom: "8px",
                              display: "block",
                            }}
                          >
                            Amount Type
                          </label>
                          <select
                            value={values.partialPayType}
                            onChange={(e) =>
                              setFieldValue("partialPayType", e.target.value)
                            }
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "8px",
                              border: "1px solid #E2E8F0",
                              fontSize: "14px",
                              background: "#FFF",
                            }}
                          >
                            {/* <option value="fixed">Fixed Amount</option> */}
                            <option value="percentage">Percentage (%)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "30px",
                }}
              >
                <button
                  type="button"
                  onClick={() => submitForm()}
                  style={{
                    background: "#16A34A",
                    color: "#FFF",
                    padding: "12px 32px",
                    borderRadius: "8px",
                    border: "none",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(22, 163, 74, 0.2)",
                  }}
                >
                  Save All Settings
                </button>
              </div>
            </div>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
};

export default OrderSettings;
